import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  ChatbotExtractRequest,
  ChatMessage,
  DropdownOption,
  FollowUpQuestion,
  RecommendedAnswer
} from './chatbot.models';
import {
  buildMeetingTypeExtractRequest,
  buildStartDateExtractRequest,
  buildStartDateTimeExtractRequest,
  buildStartTimeExtractRequest,
  hasDateQuickOptions,
  hasMeetingDetails,
  hasMeetingTypeOptions,
  hasTimeQuickOptions,
  needsDatePicker,
  needsTimePicker
} from './chatbot-message.util';

@Component({
  selector: 'app-chatbot-messages',
  templateUrl: './chatbot-messages.component.html',
  styleUrls: ['./chatbot-messages.component.css']
})
export class ChatbotMessagesComponent {
  @Input() messages: ChatMessage[] = [];
  @Input() isLoading = false;
  @Input() fontSize = 14;
  @Input() dashboardWidget = false;

  @Output() recommendedAnswerSelected = new EventEmitter<RecommendedAnswer>();
  @Output() followUpQuestionSelected = new EventEmitter<FollowUpQuestion>();
  @Output() extractInputSelected = new EventEmitter<{
    request: ChatbotExtractRequest;
    displayLabel?: string;
  }>();

  hasMeetingDetails = hasMeetingDetails;
  needsDatePicker = needsDatePicker;
  needsTimePicker = needsTimePicker;
  hasDateQuickOptions = hasDateQuickOptions;
  hasTimeQuickOptions = hasTimeQuickOptions;
  hasMeetingTypeOptions = hasMeetingTypeOptions;

  dateInputByIndex: Record<number, string> = {};
  timeInputByIndex: Record<number, string> = {};
  /** For dropdown binding (option id). */
  selectedMeetingTypeByIndex: Record<number, string> = {};
  /** For API payload (meeting type display value/label). */
  selectedMeetingTypePayloadByIndex: Record<number, string> = {};
  selectedDateValueByIndex: Record<number, string> = {};
  selectedTimeValueByIndex: Record<number, string> = {};

  onRecommendedAnswer(option: RecommendedAnswer, msg: ChatMessage): void {
    if (msg.optionsDisabled || this.isLoading) {
      return;
    }
    this.recommendedAnswerSelected.emit(option);
  }

  onFollowUpQuestion(question: FollowUpQuestion, msg: ChatMessage): void {
    if (msg.optionsDisabled || this.isLoading) {
      return;
    }
    this.followUpQuestionSelected.emit(question);
  }

  private emitExtract(request: ChatbotExtractRequest, displayLabel?: string): void {
    this.extractInputSelected.emit({ request, displayLabel });
  }

  onMeetingTypeSelect(value: string, msg: ChatMessage, index: number): void {
    if (!value || msg.optionsDisabled || this.isLoading || !msg.contextualData?.meeting_type_options) {
      return;
    }
    this.selectedMeetingTypeByIndex[index] = value;
    const option = msg.contextualData.meeting_type_options.find((o) => o.id === value);
    if (option && !this.shouldDeferMeetingTypeSubmit(msg)) {
      this.selectedMeetingTypePayloadByIndex[index] = option.label || option.id;
      this.emitExtract(
        buildMeetingTypeExtractRequest(this.selectedMeetingTypePayloadByIndex[index]),
        option.label
      );
      return;
    }
    if (option) {
      this.selectedMeetingTypePayloadByIndex[index] = option.label || option.id;
    }
  }

  onDateOptionSelect(value: string, msg: ChatMessage, index: number): void {
    if (!value || !msg.contextualData?.date_options) {
      return;
    }
    this.selectedDateValueByIndex[index] = value;
    const option = msg.contextualData.date_options.find((o) => o.id === value);
    if (option) {
      const dateValue = option.id || option.label;
      if (this.isCompositeMeetingForm(msg)) {
        return;
      }
      this.emitExtract(buildStartDateExtractRequest(dateValue), option.label);
    }
  }

  onTimeOptionSelect(value: string, msg: ChatMessage, index: number): void {
    if (!value || !msg.contextualData?.time_options) {
      return;
    }
    this.selectedTimeValueByIndex[index] = value;
    const option = msg.contextualData.time_options.find((o) => o.id === value);
    if (option) {
      const timeValue = option.id || option.label;
      if (this.isCompositeMeetingForm(msg)) {
        return;
      }
      this.emitExtract(buildStartTimeExtractRequest(timeValue), option.label);
    }
  }

  onDateQuickOption(option: DropdownOption, msg: ChatMessage, index: number): void {
    if (msg.optionsDisabled || this.isLoading) {
      return;
    }
    this.selectedDateValueByIndex[index] = option.id || option.label;
    if (this.isCompositeMeetingForm(msg)) {
      return;
    }
    this.emitExtract(buildStartDateExtractRequest(option.id || option.label), option.label);
  }

  onTimeQuickOption(option: DropdownOption, msg: ChatMessage, index: number): void {
    if (msg.optionsDisabled || this.isLoading) {
      return;
    }
    this.selectedTimeValueByIndex[index] = option.id || option.label;
    if (this.isCompositeMeetingForm(msg)) {
      return;
    }
    this.emitExtract(buildStartTimeExtractRequest(option.id || option.label), option.label);
  }

  optionCategoryClass(category: string | undefined): string {
    if (!category) {
      return '';
    }
    return `chat-option-${String(category).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  }

  useDropdown<T>(items: T[] | null | undefined): boolean {
    return Array.isArray(items) && items.length > 4;
  }

  onRecommendedDropdownChange(value: string, msg: ChatMessage): void {
    if (!value || !msg.recommendedAnswers?.length) {
      return;
    }
    const idx = Number(value);
    if (Number.isNaN(idx) || idx < 0 || idx >= msg.recommendedAnswers.length) {
      return;
    }
    this.onRecommendedAnswer(msg.recommendedAnswers[idx], msg);
  }

  onFollowUpDropdownChange(value: string, msg: ChatMessage): void {
    if (!value || !msg.followUpQuestions?.length) {
      return;
    }
    const idx = Number(value);
    if (Number.isNaN(idx) || idx < 0 || idx >= msg.followUpQuestions.length) {
      return;
    }
    this.onFollowUpQuestion(msg.followUpQuestions[idx], msg);
  }

  showCombinedDateTimePickers(msg: ChatMessage): boolean {
    return needsDatePicker(msg.contextualData) && needsTimePicker(msg.contextualData);
  }

  isCompositeMeetingForm(msg: ChatMessage): boolean {
    return this.shouldDeferMeetingTypeSubmit(msg);
  }

  shouldDeferMeetingTypeSubmit(msg: ChatMessage): boolean {
    return hasMeetingTypeOptions(msg.contextualData) && (
      needsDatePicker(msg.contextualData) ||
      needsTimePicker(msg.contextualData) ||
      hasDateQuickOptions(msg.contextualData) ||
      hasTimeQuickOptions(msg.contextualData)
    );
  }

  isLatestBotMessage(index: number): boolean {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i]?.role === 'bot') {
        return i === index;
      }
    }
    return false;
  }

  canSubmitCombined(index: number, msg: ChatMessage): boolean {
    if (hasMeetingTypeOptions(msg.contextualData) && !this.selectedMeetingTypeByIndex[index]) {
      return false;
    }
    return !!(this.dateInputByIndex[index] && this.timeInputByIndex[index]);
  }

  canSubmitContextualForm(index: number, msg: ChatMessage): boolean {
    if (hasMeetingTypeOptions(msg.contextualData) && !this.selectedMeetingTypeByIndex[index]) {
      return false;
    }
    if (needsDatePicker(msg.contextualData) && !this.dateInputByIndex[index]) {
      return false;
    }
    if (hasDateQuickOptions(msg.contextualData) && !this.selectedDateValueByIndex[index]) {
      return false;
    }
    if (needsTimePicker(msg.contextualData) && !this.timeInputByIndex[index]) {
      return false;
    }
    if (hasTimeQuickOptions(msg.contextualData) && !this.selectedTimeValueByIndex[index]) {
      return false;
    }
    return true;
  }

  submitDate(index: number, msg: ChatMessage): void {
    if (this.isLoading) {
      return;
    }
    const raw = (this.dateInputByIndex[index] || '').trim();
    if (!raw) {
      return;
    }
    if (this.isCompositeMeetingForm(msg)) {
      this.submitContextualForm(index, msg);
      return;
    }
    this.emitExtract(buildStartDateExtractRequest(raw));
  }

  submitTime(index: number, msg: ChatMessage): void {
    if (this.isLoading) {
      return;
    }
    const raw = (this.timeInputByIndex[index] || '').trim();
    if (!raw) {
      return;
    }
    if (this.isCompositeMeetingForm(msg)) {
      this.submitContextualForm(index, msg);
      return;
    }
    this.emitExtract(buildStartTimeExtractRequest(raw));
  }

  submitDateAndTime(index: number, msg: ChatMessage): void {
    if (this.isLoading) {
      return;
    }
    const date = (this.dateInputByIndex[index] || '').trim();
    const time = (this.timeInputByIndex[index] || '').trim();
    if (!date || !time) {
      return;
    }
    if (hasMeetingTypeOptions(msg.contextualData) && !this.selectedMeetingTypeByIndex[index]) {
      return;
    }
    const request = buildStartDateTimeExtractRequest(date, time);
    const meetingType = this.selectedMeetingTypePayloadByIndex[index];
    if (meetingType) {
      request.meeting_type = meetingType;
    }
    this.emitExtract(request);
  }

  submitContextualForm(index: number, msg: ChatMessage): void {
    if (this.isLoading || !this.canSubmitContextualForm(index, msg)) {
      return;
    }
    const request: ChatbotExtractRequest = {};
    if (hasMeetingTypeOptions(msg.contextualData) && this.selectedMeetingTypePayloadByIndex[index]) {
      request.meeting_type = this.selectedMeetingTypePayloadByIndex[index];
    }
    if (needsDatePicker(msg.contextualData)) {
      request.start_date = this.dateInputByIndex[index];
    } else if (hasDateQuickOptions(msg.contextualData)) {
      request.start_date = this.selectedDateValueByIndex[index];
    }
    if (needsTimePicker(msg.contextualData)) {
      request.start_time = this.timeInputByIndex[index];
    } else if (hasTimeQuickOptions(msg.contextualData)) {
      request.start_time = this.selectedTimeValueByIndex[index];
    }
    this.emitExtract(request);
  }
}
